import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import CTASection from "../components/CTASection";
import Testimonials from "../components/Testimonials";
import { useState } from "react";

const PLANS = {
  monthly: [
    { name: "Starter", price: 0, desc: "For solo founders just starting out", features: ["Up to 100 products", "1 staff account", "Basic POS & receipts", "Email support", "Brivo subdomain"] },
    { name: "Pro", price: 29, desc: "For growing teams ready to scale", features: ["Unlimited products", "Up to 10 staff accounts", "Multi-warehouse stock", "Advanced analytics", "Priority chat support", "Custom domain", "WooCommerce sync"], popular: true },
    { name: "Business", price: 79, desc: "For established multi-location ops", features: ["Everything in Pro", "Unlimited staff", "Multi-location dashboard", "Custom roles", "API & webhooks", "Phone support", "Dedicated onboarding"] },
    { name: "Enterprise", price: "Custom", desc: "For large or regulated businesses", features: ["Everything in Business", "SSO + 2FA", "99.99% SLA", "Dedicated CSM", "Custom contracts", "On-prem option", "White-glove migration"] },
  ],
  annual: [
    { name: "Starter", price: 0, desc: "For solo founders just starting out", features: ["Up to 100 products", "1 staff account", "Basic POS & receipts", "Email support", "Brivo subdomain"] },
    { name: "Pro", price: 23, desc: "For growing teams ready to scale", features: ["Unlimited products", "Up to 10 staff accounts", "Multi-warehouse stock", "Advanced analytics", "Priority chat support", "Custom domain", "WooCommerce sync"], popular: true },
    { name: "Business", price: 63, desc: "For established multi-location ops", features: ["Everything in Pro", "Unlimited staff", "Multi-location dashboard", "Custom roles", "API & webhooks", "Phone support", "Dedicated onboarding"] },
    { name: "Enterprise", price: "Custom", desc: "For large or regulated businesses", features: ["Everything in Business", "SSO + 2FA", "99.99% SLA", "Dedicated CSM", "Custom contracts", "On-prem option", "White-glove migration"] },
  ],
};

const FAQ = [
  { q: "Can I change plans anytime?", a: "Yes — upgrade, downgrade, or cancel whenever you want. Pro-rated, no penalties." },
  { q: "Is there a free trial?", a: "All paid plans include a 14-day free trial. No credit card required to start." },
  { q: "What payment methods do you accept?", a: "All major cards, bank transfer, USSD, and local payment methods in 30+ countries." },
  { q: "Do you charge transaction fees?", a: "No platform fees on top of your processor's rate. What you charge is what you keep." },
  { q: "Can I import data from another system?", a: "Yes. We import from Shopify, WooCommerce, QuickBooks, CSV, and more. Free white-glove migration on Business+." },
];

export default function Pricing() {
  const [billing, setBilling] = useState("monthly");
  const plans = PLANS[billing];

  return (
    <div data-testid="pricing-page">
      <section className="hero-gradient relative overflow-hidden grain">
        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-[#E8F5E9] opacity-60 blur-3xl animate-blob" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 md:py-28 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full glass-strong border border-[#1B4332]/10 text-black text-xs font-semibold mb-6">Pricing</span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.05]">
            Simple. Honest.<br />
            <span className="italic text-black/60 font-medium">Made to scale with you.</span>
          </h1>
          <p className="mt-7 text-lg text-black/70 max-w-2xl mx-auto leading-relaxed">
            Start free. Upgrade when you outgrow it. No hidden fees, no transaction surcharges, ever.
          </p>

          <div className="mt-10 inline-flex items-center bg-white rounded-full p-1.5 border border-[#1B4332]/10 shadow-sm">
            <button
              data-testid="billing-monthly"
              onClick={() => setBilling("monthly")}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors ${billing === "monthly" ? "bg-[#1FA868] text-white" : "text-black/70"}`}
            >
              Monthly
            </button>
            <button
              data-testid="billing-annual"
              onClick={() => setBilling("annual")}
              className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors ${billing === "annual" ? "bg-[#1FA868] text-white" : "text-black/70"}`}
            >
              Annual <span className="text-xs text-[#52B788]">−20%</span>
            </button>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#FFFCF2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((p) => (
              <div
                key={p.name}
                data-testid={`pricing-plan-${p.name.toLowerCase()}`}
                className={`rounded-3xl p-7 transition-all ${
                  p.popular
                    ? "bg-[#1B4332] text-white border-2 border-[#1B4332] shadow-2xl"
                    : "bg-white border border-[#1B4332]/8 card-hover"
                }`}
              >
                {p.popular && <div className="inline-block px-3 py-1 rounded-full bg-[#FFF9E6] text-black text-xs font-semibold mb-4">Most popular</div>}
                <div className={`font-display font-bold text-2xl ${p.popular ? "text-white" : "text-black"}`}>{p.name}</div>
                <div className={`text-sm mt-1 mb-5 ${p.popular ? "text-white/70" : "text-black/60"}`}>{p.desc}</div>
                <div className={`font-display font-extrabold text-5xl ${p.popular ? "text-white" : "text-black"}`}>
                  {typeof p.price === "number" ? `$${p.price}` : p.price}
                  {typeof p.price === "number" && <span className={`text-base font-medium ${p.popular ? "text-white/60" : "text-black/50"}`}>/mo</span>}
                </div>
                <ul className="mt-6 space-y-3 mb-8">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-2 text-sm ${p.popular ? "text-white/85" : "text-black/80"}`}>
                      <Check size={16} className="mt-0.5 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={p.price === "Custom" ? "/support" : "/get-started"}
                  className={`block text-center w-full py-3 rounded-xl font-semibold transition-colors ${
                    p.popular ? "bg-white text-black hover:bg-[#FFF9E6]" : "bg-[#1FA868] text-white hover:bg-[#16834D]"
                  }`}
                >
                  {p.price === "Custom" ? "Talk to sales" : p.price === 0 ? "Get started free" : "Start free trial"}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compare */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">All plans include</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Multi-warehouse inventory",
              "Built-in POS",
              "Online storefront",
              "Mobile apps (iOS + Android)",
              "Real-time analytics",
              "Customer CRM",
              "Auto-generated invoices",
              "Multi-currency support",
              "Email support",
            ].map((f) => (
              <div key={f} className="flex items-center gap-3 p-4 bg-[#FFFCF2] rounded-xl border border-[#1B4332]/8">
                <Check size={18} className="text-black flex-shrink-0" />
                <span className="text-black/85 font-medium">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-[#FFE4E8]">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-black tracking-tight">Pricing FAQ</h2>
          </div>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="bg-white rounded-2xl p-5 group border border-[#1B4332]/8">
                <summary className="font-semibold text-black cursor-pointer flex justify-between items-center">
                  {f.q}
                  <ArrowRight size={18} className="group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-black/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <CTASection accent="cream" />
    </div>
  );
}
